/**
 * The XLMOTO tent on the apron and the party under it: the gazebo, the rig in
 * it, the DJ, and the people in front of him.
 *
 * The tent's legs are hashed. Nothing else here is - the run has never gone
 * near the apron, which is the whole point of putting a party on it.
 */

import {
  cylinder,
  decor,
  solid,
  type CylinderSpec,
  type DecorSpec,
  type DiscoSpec,
  type SolidSpec,
  type SprawlerSpec,
  type StanderSpec,
  type StrobeSpec,
} from '../world/Level';

/* --------------------------------------------------------------- teltta - */

/**
 * ==========================================================================
 *  XLMOTO-TELTTA: the sponsor's pop-up, on the shoulder of the approach
 * ==========================================================================
 *  A 3 x 3 m pop-up gazebo - four telescopic legs, a printed skirt round the
 *  eave and a black canopy over the top of it. It is what a bike shop sends to
 *  an event when it does not send a lorry, and it is the only branded thing in
 *  a pihapiiri where everything else was already here.
 *
 *  WHERE IT STANDS. It has the ground the trampoline used to have, which is
 *  the south shoulder of the run from PARKING_END to the ansalanka - and since
 *  the harkot became three pallets on a slant, that shoulder is the fork. The
 *  chain of pallets runs past its north-east corner and the way round the left
 *  goes the other side of the same corner, so the tent is the thing standing
 *  between the two lines rather than a wall beside one of them. It came 1.8 m
 *  north with the chain, because a fork nobody has to steer round is a fork
 *  in name only.
 *
 *  IT STANDS FURTHER OFF THE LINE THAN THE MAT DID, on purpose. The
 *  trampoline's corner left 0.16 m of daylight and could be hopped, so the
 *  tight version of the shoulder was always available and cost a jump. 2.15 m
 *  of canvas cannot be hopped, and a wall a hand's width off the racing line
 *  is not a corner, it is a coin toss. `Level.test.ts` measures what is
 *  actually left and holds it to a range rather than to a figure - the
 *  clearance is a property of the reference line as much as of this box, and
 *  the reference line moves whenever the ansalanka does.
 *
 *  THE WEST WALL IS OFF, AND THE COLLIDER IS STILL THE FOOTPRINT. That pair
 *  of facts used to be forbidden here, and the reason it is not any more is
 *  worth writing down, because the old reason was right.
 *
 *  The old rule was that a pop-up with a wall left off is a horseshoe, and a
 *  horseshoe beside a line you are already drifting across is somewhere to get
 *  stuck rather than something to go round - and that a pop-up *drawn* open
 *  whose collider was still a box would be worse again, because it would be a
 *  lie. Both halves still hold. What changed is what is standing in the gap:
 *  two speaker stacks against the front legs, a trestle table bridged between
 *  them with a cloth down the front of it, and a man behind that. Floor to
 *  eave, corner to corner, there is no way through the west wall of this tent
 *  - there is simply furniture there instead of canvas. A collider that says
 *  "you cannot pass" is telling the truth about a table with a DJ behind it in
 *  exactly the way it was telling the truth about a zip.
 *
 *  Which is also what makes the open front safe to draw at all: the box is
 *  what keeps a runner out of the rig, so the rig never has to be a collider
 *  and can stay dressing. `Level.test.ts` measures the fill rather than
 *  trusting this paragraph - if somebody deletes a speaker stack, the wall
 *  becomes a hole the collider is lying about, and the test goes red.
 *
 *  WEST, AND NOT ONE OF THE OTHER THREE. North faces the running line, and a
 *  crowd lying in front of an open north wall would be a row of bodies beside
 *  the racing line that are not colliders - which is the one thing dressing
 *  must never look like. East has the zip and faces the ansalanka. South faces
 *  the fence. West faces the mouth of the parking lot, which is where the run
 *  arrives from: you come off the lot at 92 and the tent is open straight at
 *  you for six metres before the line swings right past its north-east corner.
 *  The party is in front of it and therefore behind the line, all of it on the
 *  apron between the tent and the aitaus, where nobody has ever run.
 *
 *  Everything above `eave` - the skirt and the canopy - stands over the top of
 *  the collider and is drawn in `World.addTent`. A canopy is a pyramid, and
 *  every box in this file is a box.
 * ==========================================================================
 */
export const TELTTA = {
  /** Leg to leg. The standard pop-up, and the one in the photograph. */
  span: 3.0,
  /**
   * South-west corner of the footprint. The corner that matters is the other
   * one: 99.5, -16.5 is what the line goes past.
   */
  x: 96.5,
  z: -19.5,
  /**
   * Under the eave, which is what a pop-up is for: standing height inside.
   * Also 0.43 m over the player's own 1.72 m, so the canopy is out of reach
   * from the ground with the meter full - there is nothing here to climb.
   */
  eave: 2.15,
  /** Tip of the canopy over the eave. A pop-up's pitch is shallow. */
  peak: 0.72,
  /** The printed band hanging off the eave, which is where the name is. */
  valance: 0.3,
  /** How far the canopy oversails the legs. Enough to shade the skirt. */
  overhang: 0.2,
  /** A telescopic leg, in section. */
  leg: 0.055,
  /**
   * The wall that came off, as its outward normal in world XZ.
   *
   * A direction rather than a name, because `World.addTent` already walks the
   * four sides as normals and a name would have to be turned back into one.
   * West is -x: the side the run arrives from. See the block above for why it
   * is that one and not one of the other three, and `Level.test.ts` for the
   * assertion that the gap is filled.
   */
  open: [-1, 0] as [number, number],
} as const;

export const XLMOTO_TELTTA: SolidSpec = {
  ...solid(
    'xlmoto-teltta',
    'tentWall',
    TELTTA.x,
    TELTTA.z,
    TELTTA.x + TELTTA.span,
    TELTTA.z + TELTTA.span,
    TELTTA.eave,
  ),
  shape: 'tent',
};


/* ------------------------------------------------------ teltan dj-setti - */

/**
 * ==========================================================================
 *  WHAT IS IN THE TENT, AND WHO IS STILL IN FRONT OF IT
 * ==========================================================================
 *  Somebody carried a pair of decks, two stacks and a crate of lights into
 *  the sponsor's pop-up at some point in the night and has not stopped since.
 *  The west wall is off, the rig fills the gap it left (see `TELTTA`), four
 *  people are still on their feet at the front of the desk, and everybody
 *  else who came out to listen is asleep on the grass behind them.
 *
 *  ------------------------------------------------------------------------
 *   THE EQUIPMENT IS DRESSING; THE AUDIENCE IS SOLID
 *  ------------------------------------------------------------------------
 *  The equipment and effects are render-only. The audience has invisible
 *  solids derived from its poses, so both the browser and replay verifier
 *  stop the runner at the bodies. Those boxes are in the fingerprint.
 *
 *  The tent's own box keeps the runner out of the equipment. The audience
 *  stands outside it, clear of the reference route; the southern cable route
 *  must now go around the bodies or jump over the sleepers.
 *
 *  `Level.test.ts` measures both rather than trusting either sentence: it
 *  drives the recorded reference run past the whole chapter and holds its
 *  closest approach, and checks that audience collisions are fingerprinted
 *  while the equipment and effects remain render-only.
 *
 *  ------------------------------------------------------------------------
 *   THE NUMBERS ARE THE FILL
 *  ------------------------------------------------------------------------
 *  The stacks and the desk are not decoration hung on an open wall - they are
 *  what makes the open wall shut. So they are authored as a wall is: from the
 *  tent's own corner, along the tent's own span, and adding up to it. The desk
 *  is exactly the gap the two stacks leave, and the cloth down its front is
 *  what closes the last 90 cm between the table top and the grass.
 * ==========================================================================
 */
export const DJ = {
  /**
   * How far inside the open wall the rig stands: exactly the width of a leg,
   * so a cabinet is pushed up against the telescopic leg at the corner and the
   * leg fills the rest. Derived rather than typed, because the thing it has to
   * agree with is the tent - and what the two of them together have to add up
   * to is a wall with no daylight in it. `Level.test.ts` walks the wall.
   */
  inset: TELTTA.leg,
  /** A cabinet, in plan: across the opening, and back into the tent. */
  stackWidth: 0.5,
  stackDepth: 0.5,
  /** The bass bin, and the top box that sits on it. */
  bassHeight: 0.86,
  stackHeight: 1.5,
  /** The table between them: how deep it is, and what height the top is at. */
  deskDepth: 0.7,
  deskTop: 0.95,
  deskSlab: 0.05,
  /** Where the man behind it stands, measured back from the front of the desk. */
  standBack: 0.42,
  height: 1.79,
} as const;

/** The plane the rig's front faces sit on, and the corners it is built from. */
const DJ_FRONT_X = TELTTA.x + DJ.inset;
const DJ_SOUTH_Z = TELTTA.z + DJ.inset;
const DJ_NORTH_Z = TELTTA.z + TELTTA.span - DJ.inset;
/** The gap the two stacks leave, which is exactly what the desk spans. */
const DJ_DESK_MIN_Z = DJ_SOUTH_Z + DJ.stackWidth;
const DJ_DESK_MAX_Z = DJ_NORTH_Z - DJ.stackWidth;
const DJ_DESK_MID_Z = (DJ_DESK_MIN_Z + DJ_DESK_MAX_Z) / 2;
const DJ_DESK_BACK_X = DJ_FRONT_X + DJ.deskDepth;
const DJ_LEG_TOP = DJ.deskTop - DJ.deskSlab;

/** One PA stack: a bass bin with a top box on it, and a grille facing the yard. */
function telttaStack(name: string, minZ: number): DecorSpec[] {
  const maxZ = minZ + DJ.stackWidth;
  const backX = DJ_FRONT_X + DJ.stackDepth;
  return [
    decor(`${name}-bassokaappi`, 'furniture', DJ_FRONT_X, minZ, backX, maxZ, DJ.bassHeight),
    // The top box, tucked in a couple of centimetres all round: a stack is two
    // cabinets somebody lifted onto each other, and the step between them is
    // the only thing that says so from the lot.
    decor(
      `${name}-diskantti`,
      'furniture',
      DJ_FRONT_X + 0.02,
      minZ + 0.03,
      backX - 0.02,
      maxZ - 0.03,
      DJ.stackHeight - DJ.bassHeight - 0.01,
      DJ.bassHeight + 0.01,
    ),
    // And the cloth over the front of both, which is what a speaker is from
    // any distance at all. Two centimetres proud of the cabinet, on the same
    // reasoning as the audi's rings: a face flush with the box it is on is a
    // face fighting the box for the same pixels.
    decor(`${name}-etusaleikko`, 'monitor', DJ_FRONT_X - 0.02, minZ + 0.04, DJ_FRONT_X, maxZ - 0.04, DJ.stackHeight - 0.08, 0.04),
  ];
}

export const DJ_PARTS: DecorSpec[] = [
  ...telttaStack('dj-kaiutin-etela', DJ_SOUTH_Z),
  ...telttaStack('dj-kaiutin-pohjoinen', DJ_DESK_MAX_Z),

  // The table, spanning exactly the gap the stacks leave. Two end panels
  // rather than four legs, because a trestle is what somebody carries to a
  // field and a table with turned legs is not.
  decor('dj-poyta-taso', 'table', DJ_FRONT_X, DJ_DESK_MIN_Z, DJ_DESK_BACK_X, DJ_DESK_MAX_Z, DJ.deskSlab, DJ_LEG_TOP),
  decor('dj-poyta-pukki-etela', 'furniture', DJ_FRONT_X + 0.06, DJ_DESK_MIN_Z + 0.04, DJ_DESK_BACK_X - 0.06, DJ_DESK_MIN_Z + 0.14, DJ_LEG_TOP),
  decor('dj-poyta-pukki-pohjoinen', 'furniture', DJ_FRONT_X + 0.06, DJ_DESK_MAX_Z - 0.14, DJ_DESK_BACK_X - 0.06, DJ_DESK_MAX_Z - 0.04, DJ_LEG_TOP),
  // The skirt down the front of it. This is the piece that finishes the wall:
  // above it is a table and a man, below it is cloth to the grass, and between
  // the two of them there is no daylight from the corner of one stack to the
  // corner of the other. See the note on the collider in `TELTTA`.
  decor('dj-poyta-etuliina', 'tentCanopy', DJ_FRONT_X, DJ_DESK_MIN_Z, DJ_FRONT_X + 0.04, DJ_DESK_MAX_Z, DJ_LEG_TOP),

  // What is on the table. Two players either side of a mixer, which is the one
  // layout anybody who has ever seen a booth will read at a glance - and the
  // reason the mixer is the narrow thing in the middle rather than the wide
  // thing on the end.
  decor('dj-soitin-etela', 'monitor', DJ_FRONT_X + 0.12, DJ_DESK_MID_Z - 0.62, DJ_FRONT_X + 0.54, DJ_DESK_MID_Z - 0.18, 0.05, DJ.deskTop),
  decor('dj-soitin-pohjoinen', 'monitor', DJ_FRONT_X + 0.12, DJ_DESK_MID_Z + 0.18, DJ_FRONT_X + 0.54, DJ_DESK_MID_Z + 0.62, 0.05, DJ.deskTop),
  decor('dj-mikseri', 'monitor', DJ_FRONT_X + 0.14, DJ_DESK_MID_Z - 0.15, DJ_FRONT_X + 0.52, DJ_DESK_MID_Z + 0.15, 0.07, DJ.deskTop),
  // The laptop the set is actually coming off, open at the back of the desk.
  // The lid is `screen`, which is the one material in the level the blackout
  // reaches into - so when the cable comes off the tolppa this goes dead with
  // every monitor in the navetta, without a line of code knowing it is here.
  decor('dj-lattari-pohja', 'monitor', DJ_DESK_BACK_X - 0.26, DJ_DESK_MID_Z - 0.16, DJ_DESK_BACK_X - 0.05, DJ_DESK_MID_Z + 0.16, 0.02, DJ.deskTop),
  decor('dj-lattari-kansi', 'screen', DJ_DESK_BACK_X - 0.07, DJ_DESK_MID_Z - 0.16, DJ_DESK_BACK_X - 0.04, DJ_DESK_MID_Z + 0.16, 0.22, DJ.deskTop + 0.02),
];

/**
 * The man behind it.
 *
 * Headphones rather than a cap, which is the whole of what has to read from
 * the lot: a figure in a tent is a figure in a tent, and a figure in a tent
 * with cans on is a DJ. `SoliDi` on the shirt because a handle goes on a shirt
 * at a LAN - the same rule the orga's `Koala` is under - and because the man
 * booked to fill a bike shop's gazebo is booked under the name he plays under
 * rather than under the shop's.
 *
 * He is pointed at his own mixer rather than out at the yard. An arm coming
 * down onto a desk is a man working; the same arm held level is a man waving,
 * and nobody in this pihapiiri is pleased to see anybody. See `pointArm`.
 */
const DJ_STAND_X = DJ_DESK_BACK_X + DJ.standBack;
const DJ_MIKSERI_AT: [number, number, number] = [
  DJ_FRONT_X + 0.33,
  DJ.deskTop + 0.07,
  DJ_DESK_MID_Z,
];

export const DJ_SOLIDI: StanderSpec = {
  name: 'dj-solidi',
  stand: [DJ_STAND_X, DJ_DESK_MID_Z],
  height: DJ.height,
  // Facing -x, straight out of the open wall and down the yard at whoever is
  // coming off the lot.
  rotationY: -Math.PI / 2,
  headphones: true,
  shirt: { text: 'SoliDi', color: '#f4f1e8', background: '#1f2430' },
  points: DJ_MIKSERI_AT,
};

/**
 * The rig itself: a mirror ball over the desk, four strobe heads and two
 * scanners. See `DiscoSpec` for why it is one struct and `DISCO` for the
 * timing; what is decided here is only where each fitting is bolted.
 *
 * The ball hangs over the *desk* rather than over the middle of the tent,
 * because the middle of the tent is where the man's head is. The two heads
 * that are on the tent are clamped to the front legs looking out and down
 * over the crowd; the two that are not are on stands out among them, aimed
 * back across the apron - which is what stops the whole show being one lit
 * rectangle seen end-on from the lot.
 */
const DISCO_BALL_X = DJ_FRONT_X + DJ.deskDepth / 2;
/**
 * How high it hangs and how big it is. Both are decided by the man underneath:
 * a 38 cm ball at chest height over a desk is a ball with somebody's face
 * behind it, so it is small and it is up under the canopy - which is where one
 * gets hung anyway, because that is the only thing in a pop-up to hang it from.
 */
const DISCO_BALL_Y = 1.82;
const DISCO_BALL_R = 0.15;

export const TELTAN_VALOT: DiscoSpec = {
  name: 'teltan-valot',
  ball: { center: { x: DISCO_BALL_X, y: DISCO_BALL_Y, z: DJ_DESK_MID_Z }, radius: DISCO_BALL_R },
  // From the top of the ball to the underside of the canopy, so the rod is
  // derived from the two things it actually joins.
  hanger: TELTTA.eave - (DISCO_BALL_Y + DISCO_BALL_R),
  strobes: [
    {
      name: 'strobo-teltta-etela',
      lens: { x: DJ_FRONT_X + 0.04, y: 1.98, z: TELTTA.z + 0.12 },
      // Out of the front and swung a little south, i.e. across the apron
      // rather than straight down the yard at the man running towards it.
      rotationY: -Math.PI / 2 + 0.38,
      pitch: 0.44,
      throwLength: 5.2,
      spread: 2.4,
      phase: 0,
      color: 0xffffff,
    },
    {
      name: 'strobo-teltta-pohjoinen',
      lens: { x: DJ_FRONT_X + 0.04, y: 1.98, z: TELTTA.z + TELTTA.span - 0.12 },
      rotationY: -Math.PI / 2 - 0.3,
      pitch: 0.44,
      throwLength: 5.2,
      spread: 2.4,
      phase: 0.25,
      color: 0xe8f1ff,
    },
    {
      name: 'strobo-jalusta-etela',
      lens: { x: 95.4, y: 1.62, z: -22.2 },
      // Back north-west across the crowd, at the people between it and the
      // tent rather than at the tent.
      rotationY: -0.61,
      pitch: 0.3,
      throwLength: 4.4,
      spread: 2.2,
      phase: 0.5,
      color: 0xfff2d8,
    },
    {
      name: 'strobo-jalusta-lansi',
      lens: { x: 93.1, y: 1.62, z: -19.8 },
      rotationY: 2.42,
      pitch: 0.3,
      throwLength: 4.4,
      spread: 2.2,
      phase: 0.75,
      color: 0xffffff,
    },
  ],
  lasers: [
    {
      // On top of the north stack, fanning out of the open wall and tipped up
      // enough to pass over the heads of everybody lying in front of it.
      name: 'laser-vihrea',
      lens: { x: DJ_FRONT_X + 0.24, y: DJ.stackHeight + 0.02, z: DJ_DESK_MAX_Z + DJ.stackWidth / 2 },
      rotationY: -Math.PI / 2,
      pitch: 0.15,
      beams: 7,
      fan: 0.82,
      length: 11.0,
      phase: 0,
      color: 0x3dff77,
    },
    {
      name: 'laser-punainen',
      lens: { x: DJ_FRONT_X + 0.24, y: DJ.stackHeight + 0.02, z: DJ_SOUTH_Z + DJ.stackWidth / 2 },
      rotationY: -Math.PI / 2,
      pitch: 0.21,
      beams: 7,
      fan: 0.66,
      length: 11.0,
      phase: 0.5,
      color: 0xff3350,
    },
  ],
};

/**
 * The fittings themselves, derived from the lenses above rather than typed a
 * second time.
 *
 * A lamp body that is not behind its own lens is a lamp nobody hung, and the
 * two would drift apart the first time a head was aimed somewhere else. So the
 * body is set back from the lens along the direction the head is pointed - the
 * same trick the orga's arm is aimed by, one step simpler because there is
 * nothing here to bend.
 */
const STROBE_BODY = 0.11;

function strobeBody(head: StrobeSpec): { x: number; z: number } {
  return {
    x: head.lens.x - Math.sin(head.rotationY) * STROBE_BODY,
    z: head.lens.z - Math.cos(head.rotationY) * STROBE_BODY,
  };
}

export const TELTAN_STROBOT: DecorSpec[] = TELTAN_VALOT.strobes.map((head) => {
  const at = strobeBody(head);
  return decor(
    `${head.name}-runko`,
    'monitor',
    at.x - 0.1,
    at.z - 0.1,
    at.x + 0.1,
    at.z + 0.1,
    0.2,
    head.lens.y - 0.1,
  );
});

/**
 * And the two tripods, for the two heads that are not clamped to a tent leg.
 * Filtered off the rig rather than listed, so a head that is moved onto or off
 * the tent takes its stand with it.
 */
export const TELTAN_JALUSTAT: CylinderSpec[] = TELTAN_VALOT.strobes
  .filter((head) => head.name.startsWith('strobo-jalusta'))
  .flatMap((head) => {
    const at = strobeBody(head);
    return [
      cylinder(`${head.name}-tanko`, 'metal', at.x, at.z, 0.022, head.lens.y - 0.1, 0, {
        radialSegments: 8,
      }),
      cylinder(`${head.name}-jalka`, 'metal', at.x, at.z, 0.22, 0.035, 0, {
        radialSegments: 10,
      }),
    ];
  });

/**
 * The savukone, and the only reason any of the above is visible at nine in the
 * morning.
 *
 * A beam in clear air cannot be seen from the side - what anybody actually
 * looks at when they look at a laser is the dust or the smoke it is crossing.
 * So the machine is not a fourth toy: it is what the strobes and the scanners
 * are aimed through, and it stands out at the tent's south-west corner with
 * its nozzle pointed across the apron so the fog lies where the light is.
 *
 * It is also the one plume in the level that is genuinely white - see
 * `SmokeSpec.color`. Everything else that smokes here is a fire.
 */
export const SAVUKONE = {
  minX: 95.9,
  maxX: 96.38,
  minZ: -19.66,
  maxZ: -19.24,
  height: 0.3,
} as const;

export const SAVUKONE_PARTS: DecorSpec[] = [
  decor('savukone', 'monitor', SAVUKONE.minX, SAVUKONE.minZ, SAVUKONE.maxX, SAVUKONE.maxZ, SAVUKONE.height),
  // The nozzle on the west end of it, which is what says which way this thing
  // is pointed without anybody having to watch the fog for a second.
  decor('savukone-suutin', 'metal', SAVUKONE.minX - 0.08, SAVUKONE.minZ + 0.13, SAVUKONE.minX, SAVUKONE.maxZ - 0.13, 0.1, 0.16),
];

/**
 * Everybody who came out to listen and did not last, on the grass in front of
 * the open wall.
 *
 * `sprawlers` retain their rotated poses. `TELTAN_IHMISET` supplies the
 * matching collision boxes using the same body dimensions as the renderer's
 * flat sleeper figures.
 *
 * `pose: 'flat'` on every one of them, and that is not a detail. The pose the
 * type was written for is the man face down over his own knees with his
 * trousers round them, and that man is `TUNTEMATON` on the pirtti floor - a
 * joke about one person, told once, at the far end of the level from here. Out
 * here it would not be that joke seven more times; it would be what the game
 * thinks a yard full of sleeping people looks like, in the one part of the
 * level a runner sees from six metres away with nothing else to look at. So
 * everybody outdoors is simply asleep, dressed, the length of himself on the
 * grass. See `SprawlerSpec.pose`.
 *
 * They are laid out as a crowd rather than a row: pointed every which way,
 * thinning towards the fence, and none of them closer to the running line than
 * the tent's own south wall already is. The nearest is at z = -19.3 with the
 * line four and a half metres north of him, which is more clearance than the
 * tent itself leaves - the whole apron is behind the obstacle, so anything on
 * it is behind the obstacle too. Both poses reach about the same distance from
 * the hips they are placed by, which is why swapping one for the other moved
 * nobody: a folded man is a metre of shins and a metre of back, and a flat one
 * is the same man unfolded onto the same ground.
 */
export const TELTAN_YLEISO: SprawlerSpec[] = [
  // The two closest to the mouth of the tent, where the fog is thickest.
  { name: 'lanittaja-teltalla-1', hips: [95.2, -19.9], rotationY: -1.9, height: 1.81, pose: 'flat' },
  { name: 'lanittaja-teltalla-2', hips: [94.1, -21.6], rotationY: 0.62, height: 1.74, pose: 'flat' },
  // The middle of the apron.
  { name: 'lanittaja-teltalla-3', hips: [96.0, -22.6], rotationY: 2.41, height: 1.86, pose: 'flat' },
  { name: 'lanittaja-teltalla-4', hips: [98.1, -21.8], rotationY: -2.6, height: 1.77, pose: 'flat' },
  // And the ones who got further out before it stopped mattering.
  { name: 'lanittaja-teltalla-5', hips: [100.2, -20.9], rotationY: 1.32, height: 1.83, pose: 'flat' },
  { name: 'lanittaja-teltalla-6', hips: [93.0, -19.3], rotationY: 1.94, height: 1.72, pose: 'flat' },
  { name: 'lanittaja-teltalla-7', hips: [97.4, -24.4], rotationY: -0.72, height: 1.8, pose: 'flat' },
];

/**
 * And the four who are still going, right at the front of the desk.
 *
 * A rig with nobody in front of it is equipment. Seven men asleep on the grass
 * behind it says the party is over, which is a different thing from what this
 * corner of the yard is for: the tent is open at the direction the run arrives
 * from, and what it is open at should be a party still happening. So half a
 * dozen paces of it are, and the rest of the apron is the morning after.
 *
 * `standers` with `cheering` set, which is the only pose in the venue that is
 * not aimed at a coordinate - there is nothing above a crowd to aim at. See
 * `StanderSpec.cheering` and `World.raiseArms`.
 *
 * WHERE THEY STAND is decided by two things and nothing else. They are west of
 * `TELTTA.x`, i.e. out on the grass rather than inside the collider that keeps
 * the runner out of the rig: a figure drawn inside that box would be a man
 * standing where the game says nobody can be. And they are in front of the
 * *desk* rather than in front of the stacks, because the gap between the two
 * cabinets is where a crowd at a rig actually collects and because it is the
 * one part of the open wall a person is not standing in front of a speaker.
 *
 * Two ranks rather than a line, and neither rank square to the other: four
 * people evenly spaced across an opening is a chorus. Each is turned to the
 * man himself rather than to the desk, derived the way the orga's shoulders
 * are, so moving him moves what they are watching.
 */
function fani(name: string, x: number, z: number, height: number): StanderSpec {
  return {
    name,
    stand: [x, z],
    height,
    rotationY: Math.atan2(DJ_STAND_X - x, DJ_DESK_MID_Z - z),
    cheering: true,
  };
}

export const TELTAN_FANIT: StanderSpec[] = [
  // The two at the front, one either side of the mixer.
  fani('fani-teltalla-1', 96.1, -18.62, 1.79),
  fani('fani-teltalla-2', 96.05, -17.42, 1.7),
  // And two a stride back, in the gaps the front two leave.
  fani('fani-teltalla-3', 95.42, -18.05, 1.86),
  fani('fani-teltalla-4', 95.5, -19.02, 1.74),
];

// Fixed body boxes: the dancing arms and hops remain presentation-only and
// cannot open frame-rate-dependent gaps in the replay simulation.
export const TELTAN_IHMISET: SolidSpec[] = [
  ...TELTAN_FANIT.map((fan): SolidSpec => {
    const [x, z] = fan.stand;
    const half = fan.height * 0.16;
    return {
      ...solid(fan.name, 'sleeper', x - half, z - half, x + half, z + half, fan.height),
      shape: 'invisible',
    };
  }),
  ...TELTAN_YLEISO.map((body): SolidSpec => {
    // World.flatSprawlerFigure offsets the sleeper's centre from its hips by
    // 6% of its height. Enclose that rotated sleeper in an axis-aligned box.
    const sin = Math.sin(body.rotationY);
    const cos = Math.cos(body.rotationY);
    const x = body.hips[0] + sin * body.height * 0.06;
    const z = body.hips[1] + cos * body.height * 0.06;
    const halfLength = body.height / 2;
    const halfWidth = body.height * 0.45 / 2;
    const halfX = Math.abs(sin) * halfLength + Math.abs(cos) * halfWidth;
    const halfZ = Math.abs(cos) * halfLength + Math.abs(sin) * halfWidth;
    return {
      ...solid(body.name, 'sleeper', x - halfX, z - halfZ, x + halfX, z + halfZ, body.height * 0.22),
      shape: 'invisible',
    };
  }),
];

/** What they were drinking, left where they stopped. */
export const TELTAN_KORIT: DecorSpec[] = [
  decor('teltan-kaljakori-1', 'furniture', 95.62, -21.1, 96.02, -20.8, 0.27),
  decor('teltan-kaljakori-2', 'furniture', 98.9, -22.75, 99.3, -22.45, 0.27),
];
